// Auth uç noktaları (mobildeki AuthRepository'nin web karşılığı).
// Tüm fonksiyonlar zarfı açıp ham veriyi döner; hata durumunda ApiError fırlatır.

import api from '../../api/client';
import { unwrap, toApiError, ApiError } from '../../api/unwrap';

// Şifre sıfırlama uçları mağazanın { errorCode } zarfı yerine makine-okunur
// bir `code` alanı döner (ör. RESET_CODE_SENT, INVALID_RESET_CODE) — hem 2xx
// hem hata cevaplarında. Sözleşme `code` alanıdır; UI bu koda göre kendi çeviri
// dosyasından metin seçer, message/error yalnızca fallback'tir. Bu yardımcı,
// hata cevabındaki `code`'u ApiError.code'a taşır.
const resetError = (error) => {
  if (error instanceof ApiError) return error;
  const res = error.response;
  if (res) {
    const body = res.data || {};
    return new ApiError(body.error || body.message || 'İstek başarısız oldu', {
      code: body.code || null,
      status: res.status,
    });
  }
  return new ApiError(
    'Sunucuya bağlanılamıyor. Lütfen internet bağlantınızı kontrol edin.',
    { status: null }
  );
};

// POST /auth/login → { accessToken, refreshToken }
export const login = async ({ emailOrPhone, password }) => {
  try {
    const response = await api.post('/auth/login', { emailOrPhone, password });
    return unwrap(response); // { accessToken, refreshToken }
  } catch (error) {
    throw toApiError(error);
  }
};

// POST /auth/register → { token, refreshToken, verifyRequest? }
export const register = async ({ email, fullName, password }) => {
  try {
    const response = await api.post('/auth/register', {
      email,
      fullName,
      password,
    });
    return unwrap(response); // { token, refreshToken, verifyRequest? }
  } catch (error) {
    throw toApiError(error);
  }
};

// POST /auth/verify → { email, code }. Başarıda 200, hatada 400 (error mesajı).
export const verifyEmail = async ({ email, code }) => {
  try {
    await api.post('/auth/verify', { email, code });
    return true;
  } catch (error) {
    throw toApiError(error);
  }
};

// POST /auth/resendCode → { email } → { verifyRequest } (429 rate limit)
export const resendCode = async (email) => {
  try {
    const response = await api.post('/auth/resendCode', { email });
    return unwrap(response); // { verifyRequest: { email, sessionTimeOut, ... } }
  } catch (error) {
    throw toApiError(error);
  }
};

// --- Şifremi unuttum akışı (3 uç; hepsi public, Authorization gerekmez) ---
// Başarıda sonuç `code`'unu (string) döner; hatada resetError ile ApiError
// fırlatır (err.code = INVALID_RESET_CODE / EXPIRED_RESET_CODE / ...).

// POST /auth/forgotPassword → 200 RESET_CODE_SENT | 429 RATE_LIMITED | 400 MAIL_SEND_FAILED
export const forgotPassword = async (email) => {
  try {
    const response = await api.post('/auth/forgotPassword', { email });
    return response.data?.code || 'RESET_CODE_SENT';
  } catch (error) {
    throw resetError(error);
  }
};

// POST /auth/verifyResetCode → 200 RESET_CODE_VALID | 400 INVALID_RESET_CODE / EXPIRED_RESET_CODE
// Kodu TÜKETMEZ; aynı kod resetPassword'a tekrar gönderilir.
export const verifyResetCode = async ({ email, code }) => {
  try {
    const response = await api.post('/auth/verifyResetCode', { email, code });
    return response.data?.code || 'RESET_CODE_VALID';
  } catch (error) {
    throw resetError(error);
  }
};

// POST /auth/resetPassword → 200 PASSWORD_RESET_SUCCESS | 400 WEAK_PASSWORD / INVALID_RESET_CODE / EXPIRED_RESET_CODE
export const resetPassword = async ({ email, code, newPassword }) => {
  try {
    const response = await api.post('/auth/resetPassword', {
      email,
      code,
      newPassword,
    });
    return response.data?.code || 'PASSWORD_RESET_SUCCESS';
  } catch (error) {
    throw resetError(error);
  }
};
